import { SaveOutlined } from '@ant-design/icons'
import { Button, InputNumber, Table } from 'antd'
import * as R from 'ramda'
import React, { createRef, RefObject } from 'react'
import { compose } from 'recompose'

import { printFlightNumber } from '../../helpers/flights'
import authorize from '../higher-order/authorize'

import { Flight, FlightId } from '../../schemas/Flight'
import { PlayerId } from '../../schemas/Player'
import { Playground } from '../../schemas/Playground'
import { FlightResults } from '../../schemas/Results'
import { TenantId } from '../../schemas/Tenant'
import { TournamentId } from '../../schemas/Tournament'

import prepareFlightResults from './prepareFlightResults'

const minShots = 0
const maxShots = 7

type RowItem = {
  key: number
  results: {
    [playerId: string]: number
  }
}

type FlightResultsState = {
  dataSource: RowItem[]
  currentPlayerId?: PlayerId
  currentKey?: number
}

type FlightResultsOuterProps = {
  tenant: TenantId
  tournamentId: TournamentId
  flightId: FlightId
  playground: Playground
  onCancel(): void
  afterSave(): void
}

type FlightResultsInnerProps = FlightResultsOuterProps & {
  playground: Playground
  flight?: Flight
  flightResults: FlightResults
  saveAllResultsInFlight(flight: Flight, dataSource: RowItem[])
}

class FlightResultsForm extends React.Component<FlightResultsInnerProps, FlightResultsState> {
  inputRefs: {
    [id: string]: RefObject<FIXME> | null
  } = {}
  submitRef: HTMLElement | null

  constructor(props: any) {
    super(props)
    const { flight, flightResults, playground } = props
    const { round, players } = flight
    this.state = {
      dataSource: R.range(0, playground.holes).map((hole) => ({
        key: hole,
        refs: players.map(() => React.createFactory),
        ...players.reduce(
          (acc, playerId) => ({
            ...acc,
            [playerId]: flightResults[`${playerId}_${round}`].results[hole],
          }),
          {},
        ),
      })),
      currentPlayerId: flight.players[0],
      currentKey: 0,
    }
  }

  saveResults = () => {
    const { flight, saveAllResultsInFlight, afterSave } = this.props
    if (flight) {
      saveAllResultsInFlight(flight, this.state.dataSource).then(() => afterSave())
    }
  }

  onCellChange = (key, playerId) => (value: number) => {
    const { playground, flight } = this.props
    const { dataSource } = this.state
    const targetRow = dataSource[key]
    // must validate that the value is within the allowed bounds

    if (flight && targetRow && minShots <= value && value <= maxShots) {
      const players = flight.players
      const nextKey = key + 1
      const currentPlayerIndex = players.indexOf(playerId)
      const nextPlayerIndex =
        nextKey === playground.holes ? currentPlayerIndex + 1 : currentPlayerIndex

      const modifiedDataSource = R.set(R.lensPath([key, playerId]), value ? value : 0, dataSource)

      if (currentPlayerIndex < players.length) {
        this.setState({
          dataSource: modifiedDataSource,
          currentPlayerId: players[nextPlayerIndex],
          currentKey: nextKey % playground.holes,
        })
      } else {
        this.setState({
          dataSource: modifiedDataSource,
          currentKey: undefined,
          currentPlayerId: undefined,
        })
      }
    }
  }

  onClick = (currentPlayerId, currentKey) => () => {
    this.setState({
      ...this.state,
      currentPlayerId,
      currentKey,
    })
  }

  getColumns = () => {
    const { flight, flightResults, playground } = this.props
    const { round, players } = flight!

    const tabIndex = (key, playerId) =>
      (players.indexOf(playerId) * playground.holes + key + 1).toString()

    const resultColumns = players.map((playerId) => ({
      title: flightResults[`${playerId}_${round}`].name,
      dataIndex: playerId,
      width: '15em',
      render: (value, record) => {
        const ref = this.inputRefs[`${record.key}_${playerId}`] || createRef()
        this.inputRefs[`${record.key}_${playerId}`] = ref

        return (
          <div onClick={this.onClick(playerId, record.key)}>
            <InputNumber
              tabIndex={tabIndex(record.key, playerId)}
              value={value !== 0 ? value : undefined}
              ref={ref}
              min={minShots}
              max={maxShots}
              size="small"
              precision={0}
              step={0}
              onChange={this.onCellChange(record.key, playerId)}
            />
          </div>
        )
      },
    }))

    return [
      {
        title: `#`,
        dataIndex: 'key',
        width: '5em',
        render: (text) => text + 1,
      },
      ...resultColumns,
    ]
  }

  getSumColumns = () => {
    const { flight } = this.props
    const { players } = flight!

    const sumColumns = players.map((playerId) => ({
      dataIndex: playerId,
      width: '15em',
    }))

    return [
      {
        dataIndex: 'key',
        width: '5em',
      },
      ...sumColumns,
    ]
  }

  getSumDataSource = () => {
    const { flight } = this.props
    const { players } = flight!

    return [
      players.reduce(
        (acc, playerId) => ({
          ...acc,
          [playerId]: R.sum(this.state.dataSource.map((row) => row[playerId])),
        }),
        {
          key: 'Sum',
        },
      ),
    ]
  }

  componentDidUpdate(prevProps, prevState) {
    const previousKey = prevState.currentHole
    const previousPlayerId = prevState.currentPlayerId
    const { currentKey, currentPlayerId } = this.state
    if (
      !R.isNil(currentKey) &&
      !R.isNil(currentPlayerId) &&
      (previousKey !== currentKey || previousPlayerId !== currentPlayerId)
    ) {
      const inputRef = this.inputRefs[`${currentKey}_${currentPlayerId}`]
      inputRef?.current?.focus()
    }
  }

  render() {
    const { flight, playground, onCancel } = this.props
    const { players = [] } = flight || {}
    const { dataSource } = this.state

    const submitTabIndex = players.length * playground.holes + 1

    const tableFooter = () => (
      <Button.Group>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          tabIndex={submitTabIndex}
          onClick={this.saveResults}
          ref={(button) => {
            this.submitRef = button
          }}
          type="primary"
          icon={<SaveOutlined />}>
          Save
        </Button>
      </Button.Group>
    )

    return (
      <div className="FlightResults">
        <h2>
          Edit Results {flight && ` - Round ${flight.round} - Flight ${printFlightNumber(flight)}`}
        </h2>

        <Table
          size="small"
          pagination={false}
          dataSource={dataSource}
          columns={this.getColumns()}
        />
        <Table
          showHeader={false}
          dataSource={this.getSumDataSource()}
          columns={this.getSumColumns()}
          pagination={false}
          size="small"
          footer={tableFooter}
        />
      </div>
    )
  }
}

export default compose<FlightResultsInnerProps, FlightResultsOuterProps>(
  authorize('admin'),
  prepareFlightResults,
)(FlightResultsForm)



// WEBPACK FOOTER //
// ./src/components/tournament/FlightResults.tsx